import { ExternalLink, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { formatCurrency } from "@/components/deal-metrics/shared";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface ShopifyDrillDownRequest {
  metric: "popup_units" | "web_units" | "total_units" | "gross_sales" | "popup_gross_sales";
  month?: string;
  label: string;
}

interface ShopifyOrder {
  shopify_order_id: string;
  order_number: string | null;
  email: string | null;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  shopify_created_at: string | null;
  financial_status: string | null;
  fulfillment_status: string | null;
  total_price: number | null;
  source_name: string | null;
  currency: string | null;
}

interface ShopifyOrderDrillDownSheetProps {
  drillDown: ShopifyDrillDownRequest | null;
  onClose: () => void;
  ownerId: string;
  startDate: string;
  endDate: string;
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function customerLabel(o: ShopifyOrder): string {
  const name = [o.customer_first_name, o.customer_last_name].filter(Boolean).join(" ");
  return name || o.customer_email || o.email || "—";
}

function sourceBadge(source: string | null): string {
  if (!source) return "—";
  if (source === "pos") return "Pop Up";
  if (source === "web") return "Web";
  return source;
}

export function ShopifyOrderDrillDownSheet({
  drillDown,
  onClose,
  ownerId,
  startDate,
  endDate,
}: ShopifyOrderDrillDownSheetProps) {
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchOrders = useCallback(async () => {
    if (!drillDown) return;
    setLoading(true);
    setSearch("");
    try {
      const params = new URLSearchParams({ startDate, endDate, metric: drillDown.metric });
      if (drillDown.month) params.set("month", drillDown.month);

      const res = await fetch(`/api/owner-metrics/${ownerId}/created-drill-down?${params}`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [drillDown, ownerId, startDate, endDate]);

  useEffect(() => {
    if (drillDown) fetchOrders();
  }, [drillDown, fetchOrders]);

  const filtered = search
    ? orders.filter((o) => {
        const q = search.toLowerCase();
        return (
          o.order_number?.toLowerCase().includes(q) ||
          customerLabel(o).toLowerCase().includes(q) ||
          o.email?.toLowerCase().includes(q)
        );
      })
    : orders;

  return (
    <Sheet open={!!drillDown} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            {drillDown?.label}
            {!loading && (
              <Badge variant="secondary" className="text-xs">
                {orders.length}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={`sdd-sk-${i}`} className="h-10 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {search ? "No orders match your search." : "No orders found."}
          </p>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs">Order</TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs w-[60px]">Shop</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.shopify_order_id}>
                    <TableCell className="text-xs font-medium whitespace-nowrap">
                      {o.order_number ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate">
                      {customerLabel(o)}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">
                      {formatCurrency(o.total_price)}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {formatDateTime(o.shopify_created_at)}
                    </TableCell>
                    <TableCell className="text-xs">{sourceBadge(o.source_name)}</TableCell>
                    <TableCell className="text-xs">{o.financial_status ?? "—"}</TableCell>
                    <TableCell className="text-xs">
                      <a
                        href={`https://admin.shopify.com/orders/${o.shopify_order_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 text-brand-navy hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
